import { Participant } from 'livekit-client';

export type ParticipantLikeObject = Pick<
    Participant,
    'identity' | 'isLocal' | 'lastSpokeAt' | 'joinedAt' | 'isSpeaking'
> & { isScreenShareEnabled: boolean };

const fixedSlotsCount = 9;
const previousMemberIds = [] as string[];

const lastConnectSorting = (a: ParticipantLikeObject, b: ParticipantLikeObject) => {
    return (a.joinedAt?.getTime() ?? 0) - (b.joinedAt?.getTime() ?? 0);
};

const sorting = (a: ParticipantLikeObject, b: ParticipantLikeObject) => {
    // В начале тот, кто говорил недавно
    if (a.lastSpokeAt !== b.lastSpokeAt) {
        const aLast = a.lastSpokeAt?.getTime() ?? 0;
        const bLast = b.lastSpokeAt?.getTime() ?? 0;
        return bLast - aLast;
    }

    // В начале тот, кто раньше присоединился
    if (a.joinedAt?.getTime() !== b.joinedAt?.getTime()) {
        const aLast = a.joinedAt?.getTime() ?? 0;
        const bLast = b.joinedAt?.getTime() ?? 0;
        return aLast - bLast;
    }

    return 0;
};

/**
 * Порядок сортировки.
 *
 * Для вида сеткой:
 * 1. Локальный участник
 * 2. Кто шарит экран
 * 3. Кто говорил недавно
 * 4. Все остальные в порядке подключения к миту
 *
 * Для вида спикера/мобилки в главном окне:
 * 1. Если шарят экран - экран
 * 2. Кто говорит
 * 3. Если не шарят - кто говорил последний
 *
 * Для вида спикера/мобилки в сетке - аналогично гриду.
 * Но исключаем участника, который сейчас в главном окне.
 */

export const initialSort = <T extends ParticipantLikeObject>(baseParticipants: T[]): T[] => {
    // 1. Отделяем текущего пользователя
    const local = baseParticipants.find(({ isLocal }) => isLocal);

    if (!local) return baseParticipants;

    previousMemberIds[0] = local.identity;

    // 2. Участники с демонстрацией экрана (сортировка по времени подключения)
    const remotes = baseParticipants.filter(({ isLocal }) => !isLocal) || ([] as Participant[]);
    const screenSharers = remotes.filter(({ isScreenShareEnabled }) => isScreenShareEnabled).sort(lastConnectSorting);

    // 3. Остальные участники (сортировка по времени подключения и кто раньше говорил)
    let remainingParticipants = remotes.filter(({ isScreenShareEnabled }) => !isScreenShareEnabled).sort(sorting);

    // 4. Формируем фиксированные ячейки
    let fixedParticipants = [local, ...screenSharers];

    // 5. Добавляем самых старых участников, если остались места
    const remainingFixedSlots = fixedSlotsCount - fixedParticipants.length;
    if (remainingFixedSlots > 0) {
        fixedParticipants = [...fixedParticipants, ...remainingParticipants.slice(0, remainingFixedSlots)];
        remainingParticipants = remainingParticipants.slice(remainingFixedSlots);
    } else {
        // Если экраншареров больше, чем фиксированных слотов, обрезаем
        const leftFixedParticipants = fixedParticipants.slice(fixedSlotsCount);
        fixedParticipants = fixedParticipants.slice(0, fixedSlotsCount);
        remainingParticipants = [...leftFixedParticipants, ...remainingParticipants];
    }

    const resultParticipants = [...fixedParticipants, ...remainingParticipants];
    return resultParticipants;
};
